import { useTranslation } from 'react-i18next';
import { Autocomplete, Grid, TextField } from '@mui/material';
import { isEmpty } from 'lodash/lang';

import useGeo from '../../../../../../../../services/geo/useGeo';
import { NSC_ADDRESS } from '../../../../../../../../services/i18n/namespaces';
import { normalizePostalCode } from '../../../../../../../../services/normalizers';

import Address1Autocomplete from './Address1Autocomplete/Address1Autocomplete';
import CityAutocomplete from './CityAutocomplete/CityAutocomplete';
import AddressValidator from './AddressValidator';

export default function AddressFields({ data, setAddressField, setCountryId, setCity }) {
  const { t } = useTranslation();
  const countryId = data.countryId;
  const regionId = data.regionId;
  const { countries, regions, matchedCountry, matchedRegion } = useGeo(countryId, regionId);

  return (
    <>
      <Grid size={4}>
        <Autocomplete
          value={matchedCountry}
          onChange={function({ target }, country) {
            setCountryId(country?.id || '');
          }}
          onBlur={function(e) {
            AddressValidator.revalidate();
          }}
          fullWidth
          getOptionKey={option => option.id}
          getOptionLabel={option => option.name ?? option}
          disablePortal
          options={countries}
          renderInput={params => (
            <TextField
              {...params}
              name="countryId"
              label={t(`${NSC_ADDRESS}:country`)}
              variant="standard"
              required
              autoComplete="off"
            />
          )}
        />
      </Grid>
      <Grid size={4}>
        <Autocomplete
          disabled={isEmpty(regions)}
          value={matchedRegion}
          onChange={function({ target }, region) {
            setAddressField(`regionId`, region?.id || '');
          }}
          onBlur={function(e) {
            AddressValidator.revalidate();
          }}
          fullWidth
          getOptionKey={option => option.id}
          getOptionLabel={regionOptionLabel}
          disablePortal
          options={regions}
          renderInput={params => (
            <TextField
              {...params}
              name={`regionId`}
              label={t(`${NSC_ADDRESS}:${data.countryId === 'CA' ? 'province' : 'state'}`)}
              variant="standard"
              required
              autoComplete="off"
            />
          )}
        />
      </Grid>
      <Grid size={4}>
        <CityAutocomplete
          name="city"
          matchedCountry={matchedCountry}
          matchedRegion={matchedRegion}
          city={data.city}
          onChange={function(value) {
            setCity(value);
          }}
          onBlur={function(e) {
            AddressValidator.revalidate();
          }}
        />
      </Grid>
      <Grid size={6}>
        <Address1Autocomplete
          name={`address1`}
          matchedCountry={matchedCountry}
          matchedRegion={matchedRegion}
          city={data.city}
          address1={data.address1}
          valid={data.valid}
          onChange={function(value) {
            setAddressField(`address1`, value || '');
          }}
          onBlur={function(e) {
            AddressValidator.revalidate();
          }}
        />
      </Grid>
      <Grid size={3}>
        <TextField
          name={`address2`}
          value={data.address2 || ''}
          onChange={function({ target }) {
            setAddressField(target.name, target.value || '');
          }}
          fullWidth
          label={t(`${NSC_ADDRESS}:address2`)}
          variant="standard"
          autoComplete="off"
        />
      </Grid>
      <Grid size={3}>
        <TextField
          name={`zipCode`}
          value={data.zipCode || ''}
          onChange={function({ target }) {
            setAddressField(target.name, normalizePostalCode(target.value, data.countryId));
          }}
          onBlur={function(e) {
            AddressValidator.revalidate();
          }}
          fullWidth
          label={t(`${NSC_ADDRESS}:zip`)}
          variant="standard"
          required
          autoComplete="off"
        />
      </Grid>
    </>
  );
}

function regionOptionLabel(option) {
  let label = '';
  if (option.id) {
    label += option.id;
  }
  if (option.localName || option.name) {
    label += ` (${option.localName || option.name})`;
  }
  return label || option;
}
